import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import Layout from '../components/Layout/Layout';

interface CustomerUpdate {
  username: string;
  name?: string;
  email?: string;
  email2?: string;
  mobileNumber?: string;
  telephoneNumber?: string;
  address?: string;
  emailStatus: string;
  mobileStatus: string;
  lastDetailsUpdate?: number;
}

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [showReport, setShowReport] = useState(false);
  const [error, setError] = useState('');

  // Convert dates to timestamps for Convex queries
  const startTimestamp = new Date(startDate).setHours(0, 0, 0, 0);
  const endTimestamp = new Date(endDate).setHours(23, 59, 59, 999);
  const dailyActive = useQuery(
    api.reports.getDailyActiveCustomers,
    showReport ? { startDate: startTimestamp, endDate: endTimestamp } : "skip"
  );

  const weeklyActive = useQuery(
    api.reports.getWeeklyActiveCustomers,
    showReport ? { startDate: startTimestamp, endDate: endTimestamp } : "skip"
  );

  const queue = useQuery(
    api.reports.getMeterSubmissionQueue,
    showReport ? { startDate: startTimestamp, endDate: endTimestamp } : "skip"
  );

  const sla = useQuery(
    api.reports.getMeterSubmissionSla,
    showReport
      ? { startDate: startTimestamp, endDate: endTimestamp, warningHours: 24, breachHours: 48 }
      : "skip"
  );

  const velocity = useQuery(
    api.reports.getProfileChangeVelocity,
    showReport ? { startDate: startTimestamp, endDate: endTimestamp } : "skip"
  );
  // Convex queries - only run when showReport is true
  const summary = useQuery(
    api.reports.getSummaryStats,
    showReport ? { startDate: startTimestamp, endDate: endTimestamp } : "skip"
  );

  const customerUpdates = useQuery(
    api.reports.getCustomerUpdates,
    showReport ? { startDate: startTimestamp, endDate: endTimestamp } : "skip"
  );

  const handleGenerateReport = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setShowReport(true);
  };

  const downloadCSV = () => {
    if (!customerUpdates || customerUpdates.length === 0) {
      setError('No data to export');
      return;
    }

    const headers = [
      'Username',
      'Name',
      'Email',
      'Secondary Email',
      'Mobile Number',
      'Telephone Number',
      'Address',
      'Email Status',
      'Mobile Status',
      'Last Details Update'
    ];

    const rows = customerUpdates.map((customer: CustomerUpdate) => [
      customer.username || '',
      customer.name || '',
      customer.email || '',
      customer.email2 || '',
      customer.mobileNumber || '',
      customer.telephoneNumber || '',
      (customer.address || '').replace(/,/g, ';').replace(/\n/g, ' '),
      customer.emailStatus,
      customer.mobileStatus,
      customer.lastDetailsUpdate ? new Date(customer.lastDetailsUpdate).toLocaleDateString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row: string[]) => row.map((cell: string) => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `customer-updates-${startDate}-to-${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadExcel = async () => {
    if (!customerUpdates || customerUpdates.length === 0) {
      setError('No data to export');
      return;
    }

    // For Excel, we'll use a simple XML-based approach that Excel can open
    const headers = [
      'Username',
      'Name',
      'Email',
      'Secondary Email',
      'Mobile Number',
      'Telephone Number',
      'Address',
      'Email Status',
      'Mobile Status',
      'Last Details Update'
    ];

    let xmlContent = '<?xml version="1.0"?>\n';
    xmlContent += '<?mso-application progid="Excel.Sheet"?>\n';
    xmlContent += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n';
    xmlContent += '  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n';
    xmlContent += '  <Worksheet ss:Name="Customer Updates">\n';
    xmlContent += '    <Table>\n';

    // Header row
    xmlContent += '      <Row>\n';
    headers.forEach(header => {
      xmlContent += `        <Cell><Data ss:Type="String">${escapeXml(header)}</Data></Cell>\n`;
    });
    xmlContent += '      </Row>\n';

    // Data rows
    customerUpdates.forEach((customer: CustomerUpdate) => {
      xmlContent += '      <Row>\n';
      xmlContent += `        <Cell><Data ss:Type="String">${escapeXml(customer.username || '')}</Data></Cell>\n`;
      xmlContent += `        <Cell><Data ss:Type="String">${escapeXml(customer.name || '')}</Data></Cell>\n`;
      xmlContent += `        <Cell><Data ss:Type="String">${escapeXml(customer.email || '')}</Data></Cell>\n`;
      xmlContent += `        <Cell><Data ss:Type="String">${escapeXml(customer.email2 || '')}</Data></Cell>\n`;
      xmlContent += `        <Cell><Data ss:Type="String">${escapeXml(customer.mobileNumber || '')}</Data></Cell>\n`;
      xmlContent += `        <Cell><Data ss:Type="String">${escapeXml(customer.telephoneNumber || '')}</Data></Cell>\n`;
      xmlContent += `        <Cell><Data ss:Type="String">${escapeXml(customer.address || '')}</Data></Cell>\n`;
      xmlContent += `        <Cell><Data ss:Type="String">${escapeXml(customer.emailStatus)}</Data></Cell>\n`;
      xmlContent += `        <Cell><Data ss:Type="String">${escapeXml(customer.mobileStatus)}</Data></Cell>\n`;
      xmlContent += `        <Cell><Data ss:Type="String">${customer.lastDetailsUpdate ? new Date(customer.lastDetailsUpdate).toLocaleDateString() : ''}</Data></Cell>\n`;
      xmlContent += '      </Row>\n';
    });

    xmlContent += '    </Table>\n';
    xmlContent += '  </Worksheet>\n';
    xmlContent += '</Workbook>';

    const blob = new Blob([xmlContent], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `customer-updates-${startDate}-to-${endDate}.xls`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const escapeXml = (str: string): string => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  const loading = showReport && summary === undefined;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-shade-green-500 transition-colors duration-200"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Admin
          </button>
        </div>
        <h1 className="text-3xl font-light mb-8">Customer Update Reports</h1>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-medium font-sans mb-4">Generate Report</h2>

          <form onSubmit={handleGenerateReport} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setShowReport(false);
                  }}
                  max={endDate}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setShowReport(false);
                  }}
                  min={startDate}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="bg-[#16A34A]  text-white px-6 py-2 rounded-md hover:bg-shade-green-700 disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </form>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        {showReport && summary && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-medium font-sans mb-4">Report Summary</h2>

            <p className="text-gray-600 mb-4">
              Period: {new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-blue-900 mb-2">Customers Updated</h3>
                <p className="text-3xl font-bold text-blue-700">{summary.uniqueCustomersUpdated}</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-green-900 mb-2">Email Verifications</h3>
                <p className="text-3xl font-bold text-green-700">{summary.emailVerifications}</p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-purple-900 mb-2">Mobile Verifications</h3>
                <p className="text-3xl font-bold text-purple-700">{summary.mobileVerifications}</p>
              </div>
            </div>
            {/*  */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-blue-900 mb-2">Daily Active Customers</h3>
                <p className="text-3xl font-bold text-blue-700">{dailyActive?.activeCount ?? 0}</p>
              </div>

              <div className="bg-indigo-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-indigo-900 mb-2">Weekly Active Customers</h3>
                <p className="text-3xl font-bold text-indigo-700">{weeklyActive?.activeCount ?? 0}</p>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-amber-900 mb-2">Meter Submission Queue</h3>
                <p className="text-3xl font-bold text-amber-700">{queue?.pending ?? 0}</p>
                <p className="text-sm text-amber-800 mt-1">
                  Approved: {queue?.approved ?? 0} | Rejected: {queue?.rejected ?? 0}
                </p>
              </div>

              <div className="bg-rose-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-rose-900 mb-2">Meter Submission SLA</h3>
                <p className="text-3xl font-bold text-rose-700">{sla?.breachCount ?? 0}</p>
                <p className="text-sm text-rose-800 mt-1">
                  Warning: {sla?.warningCount ?? 0} | Avg hrs: {sla?.avgResolutionHours ?? 0}
                </p>
              </div>

              <div className="bg-emerald-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-emerald-900 mb-2">Profile Change Velocity</h3>
                <p className="text-3xl font-bold text-emerald-700">{velocity?.requestsPerDay ?? 0}</p>
                <p className="text-sm text-emerald-800 mt-1">
                  Accepted: {velocity?.accepted ?? 0} | Declined: {velocity?.declined ?? 0}
                </p>
              </div>
            </div>


            <div className="border-t pt-4">
              <h3 className="text-lg font-medium mb-3">Export Report</h3>
              <div className="flex space-x-4">
                <button
                  onClick={downloadCSV}
                  disabled={!customerUpdates || customerUpdates.length === 0}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 disabled:opacity-50"
                >
                  Download CSV
                </button>
                <button
                  onClick={downloadExcel}
                  disabled={!customerUpdates || customerUpdates.length === 0}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 disabled:opacity-50"
                >
                  Download Excel
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                The report includes all customer details that were updated during the selected period,
                along with their verification status.
                {customerUpdates && ` (${customerUpdates.length} records)`}
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReportsPage;
